import { CameraPhotos, CameraPhotoVehicles, PhotoType } from '@EcamModel/model';
import PopUpBase from '@components/PopUpBase';
import { appConfig } from '@configs/index';
import { Box, Button, Chip, Fade, IconButton, Stack, Typography } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { formatDateByTimeZone } from 'src/helpers';
import { convertWarpedBoxToCropArea } from './EvidencePhotoGroup';
import { convertToCropArea } from './PopUpPlateEditing';
import { CustomTextField } from './ModalChangePlate';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import color from '@theme/Colors';
import { mappingLabelPhotosType } from '@pages/manual-create-pcn/configs';

type GeoZonePoint = { x: number; y: number; geoZoneId: number | undefined };

type Props = {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    photoType: PhotoType;
    currentPhoto?: CameraPhotoVehicles;
    newPhoto: CameraPhotos | null;
    vehicles?: CameraPhotoVehicles[];
    selectedVehicle?: CameraPhotoVehicles | null;
    onVehicleSelect?: (v: CameraPhotoVehicles) => void;
    timezone: string;
    prevGeoZones?: GeoZonePoint[][];
    geoZoneDisplayWidth: number;
    plateInput?: string;
    onPlateInputChange?: (value: string) => void;
    loading?: boolean;
};

function CurrentPhotoPreview({
    item,
    label,
    timezone,
    prevGeoZones,
    geoZoneDisplayWidth,
}: {
    item?: CameraPhotoVehicles;
    label: string;
    timezone: string;
    prevGeoZones?: GeoZonePoint[][];
    geoZoneDisplayWidth: number;
}) {
    if (!item) {
        return (
            <Stack
                flex={1}
                alignItems="center"
                justifyContent="center"
                sx={{ bgcolor: color.grey200, borderRadius: 1, minHeight: 385 }}
            >
                <Typography variant="body2" color={color.textPrimary}>
                    {label}: No photo
                </Typography>
            </Stack>
        );
    }

    const blobUrl = `${appConfig.gateway.blobURL}${item.CameraPhoto?.BlobName}`;
    const vbW = geoZoneDisplayWidth || 1920;
    const vbH = vbW * (9 / 16);
    const rootW = 1920;
    const displayScaleSize = rootW / vbW;

    const displayCropArea = convertToCropArea({
        X1: item.X1 / displayScaleSize,
        X2: item.X2 / displayScaleSize,
        X3: item.X3 / displayScaleSize,
        X4: item.X4 / displayScaleSize,
        Y1: item.Y1 / displayScaleSize,
        Y2: item.Y2 / displayScaleSize,
        Y3: item.Y3 / displayScaleSize,
        Y4: item.Y4 / displayScaleSize,
    });

    const vehicleBBoxes = (() => {
        const ai = item.AiPlateCandidate;
        if (!ai?.car?.warpedBox) return [];
        const ca = convertWarpedBoxToCropArea(ai.car.warpedBox);
        if (!ca) return [];
        return [
            {
                x: ca.x / displayScaleSize,
                y: ca.y / displayScaleSize,
                width: ca.width / displayScaleSize,
                height: ca.height / displayScaleSize,
                plate: ai.text || '',
            },
        ];
    })();

    return (
        <Stack flex={1}>
            <Typography variant="h6" color="#555" textAlign="start">
                {label}
            </Typography>
            <Box sx={{ position: 'relative', width: '100%', borderRadius: 1, overflow: 'hidden', bgcolor: '#e0e0e0' }}>
                <TransformWrapper>
                    {({ zoomIn, zoomOut, resetTransform }) => (
                        <>
                            <Stack
                                direction="row"
                                spacing={0.3}
                                sx={{ position: 'absolute', top: 4, right: 4, zIndex: 10 }}
                            >
                                <IconButton
                                    size="small"
                                    onClick={() => zoomIn()}
                                    sx={{
                                        bgcolor: 'rgba(0,0,0,0.5)',
                                        color: '#fff',
                                        '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                                        width: 28,
                                        height: 28,
                                    }}
                                >
                                    <ZoomInIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={() => zoomOut()}
                                    sx={{
                                        bgcolor: 'rgba(0,0,0,0.5)',
                                        color: '#fff',
                                        '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                                        width: 28,
                                        height: 28,
                                    }}
                                >
                                    <ZoomOutIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={() => resetTransform()}
                                    sx={{
                                        bgcolor: 'rgba(0,0,0,0.5)',
                                        color: '#fff',
                                        '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                                        width: 28,
                                        height: 28,
                                    }}
                                >
                                    <RestartAltIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Stack>
                            <TransformComponent wrapperStyle={{ width: '100%' }} contentStyle={{ width: '100%' }}>
                                <Box sx={{ position: 'relative', width: '100%' }}>
                                    <img src={blobUrl} alt={label} style={{ width: '100%', display: 'block' }} />
                                    <svg
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            pointerEvents: 'none',
                                        }}
                                        viewBox={`0 0 ${vbW} ${vbH}`}
                                        preserveAspectRatio="xMidYMid slice"
                                    >
                                        {prevGeoZones
                                            ?.filter((f) => f[0]?.geoZoneId)
                                            .map((frame, fi) => {
                                                if (!frame?.length) return null;
                                                const pts = frame.map((p) => `${p.x},${p.y}`).join(' ');
                                                return (
                                                    <polyline
                                                        key={`gz-${fi}`}
                                                        points={pts}
                                                        stroke={color.accentPink}
                                                        strokeWidth={2}
                                                        strokeDasharray="10 10"
                                                        strokeLinejoin="round"
                                                        fill="rgba(0, 0, 0, 0.12)"
                                                    />
                                                );
                                            })}
                                        {displayCropArea && (
                                            <rect
                                                x={displayCropArea.x}
                                                y={displayCropArea.y}
                                                width={displayCropArea.width}
                                                height={displayCropArea.height}
                                                stroke="red"
                                                strokeWidth={4}
                                                fill="none"
                                            />
                                        )}
                                        {vehicleBBoxes.map((bbox, i) => {
                                            const textFontSize = 28;
                                            const padX = 5;
                                            const padY = 6;
                                            const estimatedW = bbox.plate
                                                ? bbox.plate.length * textFontSize * 0.62 + padX * 2
                                                : 0;
                                            const estimatedH = textFontSize + padY * 2;
                                            const TEXT_ABOVE_THRESHOLD = estimatedH + padY + 30;
                                            const isAbove = bbox.y > TEXT_ABOVE_THRESHOLD;
                                            const textY = isAbove
                                                ? bbox.y - padY - 4
                                                : bbox.y + bbox.height + estimatedH - padY;
                                            const rectX = bbox.x - padX;
                                            const rectY = isAbove
                                                ? bbox.y - estimatedH - 4
                                                : bbox.y + bbox.height + 5;
                                            return (
                                                <g key={`bbox-${i}`}>
                                                    <rect
                                                        x={bbox.x}
                                                        y={bbox.y}
                                                        width={bbox.width}
                                                        height={bbox.height}
                                                        stroke={color.mint}
                                                        strokeWidth={2}
                                                        fill="none"
                                                    />
                                                    {bbox.plate && (
                                                        <>
                                                            <rect
                                                                x={rectX}
                                                                y={rectY}
                                                                width={estimatedW}
                                                                height={estimatedH}
                                                                rx={3}
                                                                fill="rgba(0,0,0,0.55)"
                                                            />
                                                            <text
                                                                x={bbox.x}
                                                                y={textY}
                                                                fill={color.mint}
                                                                fontSize={textFontSize}
                                                                fontWeight={500}
                                                            >
                                                                {bbox.plate}
                                                            </text>
                                                        </>
                                                    )}
                                                </g>
                                            );
                                        })}
                                    </svg>
                                </Box>
                            </TransformComponent>
                        </>
                    )}
                </TransformWrapper>
            </Box>

            <Stack direction="row" alignItems="center" justifyContent="space-between" mt={1}>
                <Typography variant="caption" color="text.secondary">
                    {formatDateByTimeZone(item.CameraPhoto?.CaptureAt, timezone)}
                </Typography>

                {item.AiPlateCandidate?.text && (
                    <Box
                        sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: '#FFFF00',
                            border: `1px solid ${color.textSecondary}`,
                            fontSize: 14,
                            fontWeight: 'bold',
                        }}
                    >
                        {item.AiPlateCandidate?.text}
                    </Box>
                )}
            </Stack>
        </Stack>
    );
}

function NewPhotoPreview({
    item,
    vehicles,
    selectedVehicle,
    onVehicleSelect,
    label,
    timezone,
    prevGeoZones,
    geoZoneDisplayWidth,
}: {
    item?: CameraPhotos;
    vehicles?: CameraPhotoVehicles[];
    selectedVehicle?: CameraPhotoVehicles | null;
    onVehicleSelect?: (v: CameraPhotoVehicles) => void;
    label: string;
    timezone: string;
    prevGeoZones?: GeoZonePoint[][];
    geoZoneDisplayWidth: number;
}) {
    if (!item) {
        return (
            <Stack
                flex={1}
                alignItems="center"
                justifyContent="center"
                sx={{ bgcolor: color.grey200, borderRadius: 1, minHeight: 385 }}
            >
                <Typography variant="body2" color={color.textPrimary}>
                    {label}: No photo
                </Typography>
            </Stack>
        );
    }

    // CameraPhotos has BlobName directly
    const blobUrl = `${appConfig.gateway.blobURL}${item.BlobName}`;
    const vbW = geoZoneDisplayWidth || 1920;
    const vbH = vbW * (9 / 16);
    const rootW = 1920;
    const displayScaleSize = rootW / vbW;

    // Plate crop area from selected vehicle's coordinates
    const displayCropArea = selectedVehicle
        ? convertToCropArea({
              X1: selectedVehicle.X1 / displayScaleSize,
              X2: selectedVehicle.X2 / displayScaleSize,
              X3: selectedVehicle.X3 / displayScaleSize,
              X4: selectedVehicle.X4 / displayScaleSize,
              Y1: selectedVehicle.Y1 / displayScaleSize,
              Y2: selectedVehicle.Y2 / displayScaleSize,
              Y3: selectedVehicle.Y3 / displayScaleSize,
              Y4: selectedVehicle.Y4 / displayScaleSize,
          })
        : null;

    const allVehicles = vehicles ?? item.CameraPhotoVehicles ?? [];
    const vehicleBBoxes = allVehicles.flatMap((v) => {
        const ai = v.AiPlateCandidate;
        if (!ai?.car?.warpedBox) return [];
        const ca = convertWarpedBoxToCropArea(ai.car.warpedBox);
        if (!ca) return [];
        const isSelected = selectedVehicle?.Id === v.Id;
        return [
            {
                x: ca.x / displayScaleSize,
                y: ca.y / displayScaleSize,
                width: ca.width / displayScaleSize,
                height: ca.height / displayScaleSize,
                plate: v.AiPlateCandidate?.text || ai.text || '',
                isSelected,
            },
        ];
    });

    return (
        <Stack flex={1}>
            <Typography variant="h6" color="#555" textAlign="start">
                {label}
            </Typography>
            <Box sx={{ position: 'relative', width: '100%', borderRadius: 1, overflow: 'hidden', bgcolor: '#e0e0e0' }}>
                <TransformWrapper>
                    {({ zoomIn, zoomOut, resetTransform }) => (
                        <>
                            <Stack
                                direction="row"
                                spacing={0.3}
                                sx={{ position: 'absolute', top: 4, right: 4, zIndex: 10 }}
                            >
                                <IconButton
                                    size="small"
                                    onClick={() => zoomIn()}
                                    sx={{
                                        bgcolor: 'rgba(0,0,0,0.5)',
                                        color: '#fff',
                                        '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                                        width: 28,
                                        height: 28,
                                    }}
                                >
                                    <ZoomInIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={() => zoomOut()}
                                    sx={{
                                        bgcolor: 'rgba(0,0,0,0.5)',
                                        color: '#fff',
                                        '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                                        width: 28,
                                        height: 28,
                                    }}
                                >
                                    <ZoomOutIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={() => resetTransform()}
                                    sx={{
                                        bgcolor: 'rgba(0,0,0,0.5)',
                                        color: '#fff',
                                        '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                                        width: 28,
                                        height: 28,
                                    }}
                                >
                                    <RestartAltIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Stack>
                            <TransformComponent wrapperStyle={{ width: '100%' }} contentStyle={{ width: '100%' }}>
                                <Box sx={{ position: 'relative', width: '100%' }}>
                                    <img src={blobUrl} alt={label} style={{ width: '100%', display: 'block' }} />
                                    <svg
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            pointerEvents: 'none',
                                        }}
                                        viewBox={`0 0 ${vbW} ${vbH}`}
                                        preserveAspectRatio="xMidYMid slice"
                                    >
                                        {prevGeoZones
                                            ?.filter((f) => f[0]?.geoZoneId)
                                            .map((frame, fi) => {
                                                if (!frame?.length) return null;
                                                const pts = frame.map((p) => `${p.x},${p.y}`).join(' ');
                                                return (
                                                    <polyline
                                                        key={`gz-${fi}`}
                                                        points={pts}
                                                        stroke={color.accentPink}
                                                        strokeWidth={2}
                                                        strokeDasharray="10 10"
                                                        strokeLinejoin="round"
                                                        fill="rgba(0, 0, 0, 0.12)"
                                                    />
                                                );
                                            })}
                                        {/* Plate crop area — from selected vehicle */}
                                        {displayCropArea && (
                                            <rect
                                                x={displayCropArea.x}
                                                y={displayCropArea.y}
                                                width={displayCropArea.width}
                                                height={displayCropArea.height}
                                                stroke="red"
                                                strokeWidth={4}
                                                fill="none"
                                            />
                                        )}
                                        {/* Vehicle bboxes — selected highlighted, others dimmed */}
                                        {vehicleBBoxes.map((bbox, i) => {
                                            const textFontSize = 28;
                                            const padX = 5;
                                            const padY = 6;
                                            const estimatedW = bbox.plate
                                                ? bbox.plate.length * textFontSize * 0.62 + padX * 2
                                                : 0;
                                            const estimatedH = textFontSize + padY * 2;
                                            const TEXT_ABOVE_THRESHOLD = estimatedH + padY + 30;
                                            const isAbove = bbox.y > TEXT_ABOVE_THRESHOLD;
                                            const textY = isAbove
                                                ? bbox.y - padY - 4
                                                : bbox.y + bbox.height + estimatedH - padY;
                                            const rectX = bbox.x - padX;
                                            const rectY = isAbove
                                                ? bbox.y - estimatedH - 4
                                                : bbox.y + bbox.height + 5;
                                            const bboxColor = bbox.isSelected ? color.mint : 'rgba(44, 223, 247, 0.45)';
                                            const strokeW = bbox.isSelected ? 4 : 2;
                                            return (
                                                <g key={`bbox-${i}`}>
                                                    <rect
                                                        x={bbox.x}
                                                        y={bbox.y}
                                                        width={bbox.width}
                                                        height={bbox.height}
                                                        stroke={bboxColor}
                                                        strokeWidth={strokeW}
                                                        fill="none"
                                                    />
                                                    {bbox.plate && bbox.isSelected && (
                                                        <>
                                                            <rect
                                                                x={rectX}
                                                                y={rectY}
                                                                width={estimatedW}
                                                                height={estimatedH}
                                                                rx={3}
                                                                fill="rgba(0,0,0,0.55)"
                                                            />
                                                            <text
                                                                x={bbox.x}
                                                                y={textY}
                                                                fill={color.mint}
                                                                fontSize={textFontSize}
                                                                fontWeight={500}
                                                            >
                                                                {bbox.plate}
                                                            </text>
                                                        </>
                                                    )}
                                                </g>
                                            );
                                        })}
                                    </svg>
                                </Box>
                            </TransformComponent>
                        </>
                    )}
                </TransformWrapper>
            </Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mt={1}>
                <Typography variant="caption" color="text.secondary">
                    {formatDateByTimeZone(item.CaptureAt, timezone)}
                </Typography>

                {selectedVehicle && selectedVehicle.AiPlateCandidate?.text && (
                    <Box
                        sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: '#FFFF00',
                            border: `1px solid ${color.textSecondary}`,
                            fontSize: 14,
                            fontWeight: 'bold',
                        }}
                    >
                        {selectedVehicle.AiPlateCandidate?.text}
                    </Box>
                )}
            </Stack>
        </Stack>
    );
}

// ─── Dialog ───────────────────────────────────────────────────────────────────
export default function ConfirmEvidenceChangeDialog({
    open,
    onClose,
    onConfirm,
    photoType,
    currentPhoto,
    newPhoto,
    vehicles,
    selectedVehicle,
    onVehicleSelect,
    timezone,
    prevGeoZones,
    geoZoneDisplayWidth,
    plateInput,
    onPlateInputChange,
    loading,
}: Props) {
    const label = mappingLabelPhotosType[photoType] || 'Photo';
    const isPlateType = photoType === PhotoType.UnobstructedPlate;

    const resolvedVehicles = vehicles ?? newPhoto?.CameraPhotoVehicles ?? [];
    const hasSelectableVehicles = resolvedVehicles.some((v) => v.AiPlateCandidate != null);
    const vehicleRequired = hasSelectableVehicles && !selectedVehicle;

    return (
        <PopUpBase
            open={open}
            dialogProps={{
                fullWidth: true,
                maxWidth: 'lg',
                onKeyDown: (e) => {
                    if (e.key === 'Enter') {
                        e.stopPropagation();
                        onClose?.();
                        onConfirm?.();
                    } else if (e.key === 'Escape') {
                        e.stopPropagation();
                        onClose?.();
                    }
                },
                sx: {
                    '& .MuiDialog-paper': { borderRadius: '10px' },
                    zIndex: (theme) => theme.zIndex.modal + 1,
                },
            }}
            onClose={onClose}
            onConfirm={onConfirm}
            title={<Typography variant="h4">Confirm: Select as {label}</Typography>}
            desc={
                <Stack spacing={2}>
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                        {/* Left — existing evidence */}
                        <CurrentPhotoPreview
                            item={currentPhoto}
                            label={`Current ${label}`}
                            timezone={timezone}
                            prevGeoZones={prevGeoZones}
                            geoZoneDisplayWidth={geoZoneDisplayWidth}
                        />
                        <Stack alignItems="center" justifyContent="center" sx={{ pt: 20 }}>
                            <Typography variant="h5" color={color.textPrimary}>
                                →
                            </Typography>
                        </Stack>
                        {/* Right — new selection (CameraPhotos with vehicle picker) */}
                        <NewPhotoPreview
                            item={newPhoto ?? undefined}
                            vehicles={resolvedVehicles}
                            selectedVehicle={selectedVehicle}
                            onVehicleSelect={onVehicleSelect}
                            label={`New ${label}`}
                            timezone={timezone}
                            prevGeoZones={prevGeoZones}
                            geoZoneDisplayWidth={geoZoneDisplayWidth}
                        />
                    </Stack>

                    {resolvedVehicles.some((v) => v.AiPlateCandidate != null) && (
                        <Stack spacing={0.5}>
                            <Typography variant="caption" fontWeight={600} color="text.secondary">
                                Detected plates — tap to select:
                            </Typography>

                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {resolvedVehicles
                                    .filter((v) => v.AiPlateCandidate != null)
                                    .map((v) => {
                                        const isSelected = selectedVehicle?.Id === v.Id;

                                        return (
                                            <Chip
                                                key={v.Id}
                                                label={v.AiPlateCandidate?.text}
                                                size="medium"
                                                onClick={() => onVehicleSelect?.(v)}
                                                variant={'outlined'}
                                                sx={{
                                                    cursor: 'pointer',
                                                    fontWeight: isSelected ? 500 : 400,
                                                    fontSize: '1rem',

                                                    bgcolor: isSelected ? '#FFFF00' : 'transparent',
                                                    borderColor: `1px solid ${color.textSecondary}`,

                                                    '&:hover': {
                                                        bgcolor: isSelected
                                                            ? '#FFFF00'
                                                            : `1px solid ${color.textSecondary}`,
                                                    },
                                                }}
                                            />
                                        );
                                    })}
                            </Stack>
                        </Stack>
                    )}

                    {/* Plate text edit — only for UnobstructedPlate type */}
                    {isPlateType && onPlateInputChange && (
                        <Fade in timeout={500}>
                            <Stack spacing={0.5}>
                                <Typography variant="body2" fontWeight={600} color="#555">
                                    License Plate (editable)
                                </Typography>
                                <CustomTextField
                                    value={plateInput || ''}
                                    onChange={(e) => {
                                        const inputEl = e.target;
                                        const cursorPosition = inputEl.selectionStart;
                                        onPlateInputChange(inputEl.value.toUpperCase());
                                        requestAnimationFrame(() => {
                                            inputEl.setSelectionRange(cursorPosition, cursorPosition);
                                        });
                                    }}
                                    placeholder="Enter Reg"
                                    autoFocus
                                    inputProps={{ maxLength: 20 }}
                                />
                            </Stack>
                        </Fade>
                    )}
                </Stack>
            }
            customActions={
                <Stack direction="row" width="100%" justifyContent="space-between" mt={2}>
                    <Button variant="cancel" sx={{ minWidth: 90 }} onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ minWidth: 90 }}
                        disabled={loading || !newPhoto || vehicleRequired}
                        onClick={onConfirm}
                    >
                        Confirm
                    </Button>
                </Stack>
            }
        />
    );
}
